import { LinkBox, SelectField } from '@flos/react-ui';
import { useHippoText } from '../../../hooks/useHippoText';
import CoverableDetailsTemplate from '../../../templates/CoverableDetailsTemplate';
import WizardPagetemplate from '../../../templates/WizardPagetemplate';
import { useState } from 'react';
import CallMeModal from '../../../shared_components/CallMeModal/CallMeModal';
import { useRecoilValue } from 'recoil';
import { allCoverablesOnsaAtom } from '../../../recoil/atoms/allCoverablesOnsaAtom';
import { getOnsaCurrentProductSelector } from '../../../recoil/selectors/getOnsaCurrentProductSelector';
import { getCoverableFromAllOnsaCoverables, getOnsaCoverable } from '../../../utils/coverablesUtil';
import type {
    YouthContentOnsaDTO,
    PrivateContentOnsaDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/content';
import { useUpdateCoverableInformation } from '../../../recoil/hooks/useUpdateCoverableInformation';
import { useSaveClaimsHistoryInformation } from '../../../hooks/useSaveClaimsHistoryInformation';
import type { ClaimsHistoryInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import RemoveModal from '../../../shared_components/RemoveModal/RemoveModal';
import { track } from '../../../utils/trackingUtil';

const ContentTypeOfClaimStep = ({
    goNext,
    goPrevious
}: {
    goNext: () => void;
    goPrevious: () => void;
}): React.JSX.Element => {
    const { saveClaimsHistoryInformation } = useSaveClaimsHistoryInformation();
    const coverables = useRecoilValue(allCoverablesOnsaAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const { flavourName, fixedId } = currentProduct;
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const coverable = getOnsaCoverable(coverables, flavourName, fixedId) as
        | PrivateContentOnsaDTO
        | YouthContentOnsaDTO;
    const { topdanmarkClaimTypeCode = '', claimValue = 0 } = coverable;
    const getHippoText = useHippoText();

    const [showCallMeModal, setShowCallMeModal] = useState<boolean>(false);
    const [claimType, setClaimType] = useState<string>(topdanmarkClaimTypeCode);
    const [compensation, setCompensation] = useState<number>(claimValue);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const currentCoverable = getCoverableFromAllOnsaCoverables(
        coverables,
        currentProduct.fixedId,
        currentProduct.lobCode
    );

    const compensationText =
        claimType === 'Indbrud'
            ? {
                  firstOption: getHippoText('DSS-COVERABLE-INFORMATION-STEP-CLAIM-0-10000'),
                  secondOption: getHippoText('DSS-COVERABLE-INFORMATION-STEP-CLAIM-UNDER-100000'),
                  thirdOption: getHippoText('DSS-COVERABLE-INFORMATION-STEP-CLAIM-OVER-100000')
              }
            : {
                  firstOption: getHippoText('DSS-COVERABLE-INFORMATION-STEP-CLAIM-0-70000'),
                  secondOption: getHippoText('DSS-COVERABLE-INFORMATION-STEP-CLAIM-UNDER-300000'),
                  thirdOption: getHippoText('DSS-COVERABLE-INFORMATION-STEP-CLAIM-OVER-300000')
              };

    const isFormValid =
        (claimType.length > 0 && compensation > 0) ||
        (claimType === 'Skybrud' && flavourName === 'Indboforsikring til unge') ||
        claimType === 'Anden skade';

    const convertCompensationValue = (): number => {
        if (compensation === 1) return claimType === 'Indbrud' ? 5000 : 35000;
        if (claimType === 'Anden skade') return 5000;
        return claimType === 'Indbrud' ? 50000 : 175000;
    };

    const onNext = (): void => {
        if (compensation === 3) {
            setShowCallMeModal(true);
            return;
        } else if (flavourName === 'Indboforsikring til unge' && claimType === 'Skybrud') {
            setShowRemoveModal(true);
            return;
        }
        setIsLoading(true);
        const valueOfClaim = convertCompensationValue();
        setCompensation(valueOfClaim);

        updateCoverableInformation({
            claimValue: valueOfClaim,
            topdanmarkClaimTypeCode: claimType
        });

        const updatedCoverable: ClaimsHistoryInformationToUpdateDTO = {
            [flavourName === 'Indboforsikring' ? 'privateContent' : 'youthContent']: {
                fixedId: coverable.fixedId,
                numberOfClaims: coverable.numberOfClaims,
                topdanmarkClaimTypeCode: claimType,
                claimValue: valueOfClaim
            },
            productToUpdate: flavourName
        };

        void saveClaimsHistoryInformation(updatedCoverable).then(() => {
            const contentType =
                flavourName === 'Indboforsikring' ? 'privateContent' : 'youthContent';
            track(`${contentType}CoverableInformationTypeOfClaims`, [
                {
                    claimValue: valueOfClaim
                },
                {
                    topdanmarkClaimTypeCode: claimType
                }
            ]);
            setIsLoading(false);
            goNext();
        });
    };

    return (
        <WizardPagetemplate
            onPrevious={goPrevious}
            onNext={onNext}
            disableNext={!isFormValid || isLoading}
        >
            <CoverableDetailsTemplate
                header={getHippoText('DSS-COVERABLE-INFORMATION-STEP-CLAIM-HEADER')}
            >
                <div>
                    <SelectField
                        label={getHippoText('DSS-COVERABLE-INFORMATION-STEP-CLAIM-TYPE-DROPDOWN')}
                        options={['Indbrud', 'Skybrud', 'Anden skade']}
                        value={claimType}
                        onChange={(event) => {
                            setClaimType(event.target.value);
                            setCompensation(0);
                        }}
                        required
                    />
                </div>
                {(claimType === 'Skybrud' && flavourName === 'Indboforsikring til unge') ||
                claimType === 'Anden skade' ? (
                    <></>
                ) : (
                    <>
                        <p className="strong">
                            {getHippoText('DSS-COVERABLE-INFORMATION-STEP-CLAIM-SUBHEADER')}
                        </p>
                        <div className="u-display-flex u-flex-column u-flex-items-start u-gap-5">
                            <LinkBox
                                className="flos-box"
                                title={compensationText.firstOption}
                                onClick={() => {
                                    setCompensation(1);
                                }}
                            ></LinkBox>
                            <LinkBox
                                className="flos-box"
                                title={compensationText.secondOption}
                                onClick={() => {
                                    setCompensation(2);
                                }}
                            ></LinkBox>
                            <LinkBox
                                className="flos-box"
                                title={compensationText.thirdOption}
                                onClick={() => {
                                    setCompensation(3);
                                }}
                            ></LinkBox>
                        </div>
                    </>
                )}
            </CoverableDetailsTemplate>
            {showCallMeModal && (
                <CallMeModal
                    activityCode="dss_callcha_requesttoo_many_claims"
                    showModal={showCallMeModal}
                    title={getHippoText('DSS-CALLME-MODAL-PRC-MULTI-PURPOSE-HEADER')}
                    description={getHippoText('DSS-CALLME-MODAL-PRC-MULTI-PURPOSE-SUBHEADER')}
                    closeModal={() => {
                        setShowCallMeModal(false);
                    }}
                />
            )}
            {showRemoveModal && currentCoverable && (
                <RemoveModal
                    coverable={currentCoverable}
                    title={getHippoText('DSS-REMOVE-CONTINUE-MODAL-CLOUDBURST-HEADER')}
                    description={getHippoText('DSS-REMOVE-CONTINUE-MODAL-CLOUDBURST-SUBHEADER')}
                    buttonText="Fjern forsikring"
                    isRemoveAndContinueModal
                    showModal={showRemoveModal}
                    showCloseButton
                    closeModal={() => {
                        setShowRemoveModal(false);
                    }}
                />
            )}
        </WizardPagetemplate>
    );
};

export default ContentTypeOfClaimStep;
